import { Card } from "design-system/molecules/Card"
import { NoData } from "design-system/molecules/NoData"
import { TooltipData, TooltipOverlay } from "modules/dashboard/components/Treemaps/TooltipOverlay"
import { ColorByEnum } from "modules/dashboard/lib/colorBy"
import { TreemapChart } from "modules/dashboard/components/Treemaps/TreemapChart"
import { useTreemapConfig } from "modules/dashboard/hooks/useTreemapConfig"
import { Loading } from "modules/shared/components/Loading"
import { useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { BackofficeSupportTreemapDomainEnum, CanonicalTopicsCompressionDataSchema } from "schema"
import { z } from "zod"

const UNCATEGORIZED_TOPIC_ID = "-1"

type Props = {
  data: z.infer<typeof CanonicalTopicsCompressionDataSchema> | undefined
  isPending: boolean
  onTopicClick?: (id: string) => void
  domain: (typeof BackofficeSupportTreemapDomainEnum)[number]
}

export function BackofficeSupportTreemap({ data, isPending, onTopicClick, domain }: Props) {
  const { t } = useTranslation()
  const config = useTreemapConfig(domain, null, false)
  const [tooltip, setTooltip] = useState<TooltipData | null>(null)
  const [isTooltipHovered, setIsTooltipHovered] = useState(false)
  const hideTimerRef = useRef<number | null>(null)
  const orderedData = useMemo(() => {
    if (!data) return data

    const categorized = data.filter((topic) => String(topic.id) !== UNCATEGORIZED_TOPIC_ID)
    const uncategorized = data.filter((topic) => String(topic.id) === UNCATEGORIZED_TOPIC_ID)

    return [...categorized, ...uncategorized]
  }, [data])
  const hasData = (orderedData?.length ?? 0) > 0

  return (
    <Card className="p-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="text-md font-medium">{t(`dashboard.domains.${domain}`)}</span>
          <span className="text-sm text-muted-foreground">
            {t("backoffice.support.treemap.description")}
          </span>
        </div>
        <div className="h-[280px]">
          {isPending ? (
            <Loading className="h-full" />
          ) : (
            <TreemapChart
              key={`backoffice-support-treemap-${domain}`}
              data={orderedData}
              noData={!hasData}
              colorBy={config.colorBy ?? ColorByEnum.SolveRate}
              onNodeClick={(node) => {
                if (node.id === undefined) return
                const id = String(node.id)
                if (id === UNCATEGORIZED_TOPIC_ID) return
                onTopicClick?.(id)
              }}
              onTooltipChange={(nextTooltip) => {
                if (nextTooltip == null) {
                  if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
                  hideTimerRef.current = window.setTimeout(() => {
                    if (!isTooltipHovered) setTooltip(null)
                  }, 120)
                  return
                }

                if (String(nextTooltip.node.id) === UNCATEGORIZED_TOPIC_ID) {
                  if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
                  setTooltip(null)
                  return
                }

                if (hideTimerRef.current) {
                  window.clearTimeout(hideTimerRef.current)
                  hideTimerRef.current = null
                }
                setTooltip(nextTooltip)
              }}
            />
          )}
        </div>
        {!isPending && !hasData && (
          <NoData
            title={t("backoffice.support.treemap.emptyTitle")}
            description={t("backoffice.support.treemap.emptyDescription")}
          />
        )}
      </div>
      {typeof document !== "undefined" && tooltip
        ? createPortal(
            <TooltipOverlay
              key="backoffice-support-treemap-tooltip"
              domain={domain}
              tooltip={tooltip}
              scoreComponent={config.getScoreComponent?.(tooltip.node)}
              onMouseEnter={() => {
                setIsTooltipHovered(true)
                if (hideTimerRef.current) {
                  window.clearTimeout(hideTimerRef.current)
                  hideTimerRef.current = null
                }
              }}
              onMouseLeave={() => {
                setIsTooltipHovered(false)
                if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
                hideTimerRef.current = window.setTimeout(() => setTooltip(null), 120)
              }}
            />,
            document.body,
          )
        : null}
    </Card>
  )
}
